import { applyOverrides } from '../quests/score';
import { resolveStepType, type QuestSubject, type StepOpens, type StepOverrides } from '../quests/step-catalog';

/**
 * Points a new step bounty pays until an admin sets another number.
 * The editor's Points field, the All-steps confirm, and `toggleStepBounty` all read this.
 */
export const DEFAULT_STEP_BOUNTY_POINTS = 50;

/**
 * HOW FAR ALONG A MEMBER IS ON ONE BOUNTY — the step's own words, the step's own panel,
 * and the bounty's own bar.
 *
 * A bounty is one step from the catalog, so there is no second vocabulary: the label, the
 * evidence line, the why, the href and what tapping opens all come from the step, with an
 * admin's overrides applied through the same function a quest uses. The one thing a
 * bounty adds is `target` — "Rate 3" against a step whose own bar is 5 — and only a step
 * that declares its count (`metric` + `bar`) can take one.
 *
 * Pure: the caller passes the subject already windowed to what this bounty may count
 * (this period, since it went live, since she joined it), and whether a click step was
 * clicked from this bounty this period.
 */

export type BountyDefinition = {
  stepCode: string;
  /** The bounty's own bar; null = the step's. */
  target: number | null;
  stepOverrides: StepOverrides | null;
};

export type ScoredBounty = {
  stepCode: string;
  label: string;
  evidence: string;
  why: string;
  href: string;
  opens: StepOpens;
  /** The button's words, for a step shown as a message. */
  cta?: string;
  minutes?: number;
  completion: 'measured' | 'click';
  /** 0..1. */
  progress: number;
  complete: boolean;
  /** True when the step's `requires` says she cannot start it yet. */
  blocked: boolean;
  /** For a counted step: how many so far, capped at the bar, and the bar. */
  count: number | null;
  of: number | null;
};

function clamp(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

export function scoreBounty(
  definition: BountyDefinition,
  subject: QuestSubject,
  clicked: boolean
): ScoredBounty | null {
  const step = resolveStepType(definition.stepCode);
  if (!step) return null;

  const counted = step.metric !== undefined && step.bar !== undefined;
  const of = counted ? (definition.target ?? step.bar!) : null;
  const raw = counted ? Number(subject[step.metric!] ?? 0) || 0 : null;

  const progress = counted && definition.target != null ? clamp(raw! / of!) : clamp(step.progress(subject));
  const complete = step.completion === 'measured' ? progress >= 1 : clicked;
  const copy = applyOverrides(
    step,
    definition.stepOverrides,
    subject,
    definition.target != null ? { target: definition.target } : undefined
  );

  return {
    stepCode: definition.stepCode,
    ...copy,
    completion: step.completion,
    progress: complete ? 1 : progress,
    complete,
    blocked: step.requires ? !step.requires(subject) : false,
    count: raw === null ? null : Math.min(raw, of!),
    of,
  };
}
